import { DoubleRightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Descriptions, message, Popover, Spin } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';

import { fetchAPI } from '../../utils/api';

const LINE_ITEM_STATUS_LABELS: Record<string, string> = {
  OOS: 'Out of Stock',
  LOST: 'Lost',
  DAMAGED: 'Damaged',
  WRONG_PRODUCT: 'Wrong Product',
  WRONG_QUANTITY: 'Wrong Quantity',
  FAILED_DELIVERY: 'Failed Delivery',
};

const SupplierPaymentPopOver: React.FC<{
  supplierPaymentId: number;
  status?: string;
  lineItemProblemStatus?: string;
}> = props => {
  const [visible, setVisible] = useState(false);

  const STATUS_LABELS: Record<
    string,
    { label: string; title: string; message: string }
  > = {
    CANCELLED: {
      label: 'Cancelled',
      title: 'Cancelled SPID',
      message: 'This SPID has been cancelled.',
    },
    FAILED: {
      label: 'Failed',
      title: 'Failed SPID',
      message: 'This SPID has failed.',
    },
    FAILED_DELIVERY: {
      label: 'Failed Delivery',
      title: 'Failed Delivery SPID',
      message: 'This SPID delivery has failed.',
    },
  };

  const statusInfo = STATUS_LABELS[props.status || ''];
  const isSpecialStatus = !!statusInfo;

  const { isLoading, data } = useQuery<{
    loading: boolean;
    tracking_number?: string;
    order_date?: string;
    est_lead_time?: string;
  }>(
    ['supplier-payment-detail-on-order-item', props.supplierPaymentId, visible],
    async () => {
      try {
        if (!visible || isSpecialStatus) {
          return {
            loading: true,
            tracking_number: null,
            order_date: null,
            est_lead_time: null,
          };
        }
        const resp = await fetchAPI(
          `supplier-payments/${props.supplierPaymentId}/popover-detail`
        );

        return {
          loading: false,
          tracking_number: resp?.tracking_number,
          order_date: resp?.order_date,
          est_lead_time: resp?.est_lead_time,
        };
      } catch (error) {
        message.error('Failed get supplier payment pop over detail detail');
        return null;
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        loading: true,
        tracking_number: null,
        order_date: null,
        est_lead_time: null,
      },
    }
  );

  const lineItemProblemLabel =
    LINE_ITEM_STATUS_LABELS[props.lineItemProblemStatus || ''];
  const hasLineItemProblem = !isSpecialStatus && !!lineItemProblemLabel;

  const specialStatusPopoverContent = (statusInfo || hasLineItemProblem) && (
    <div style={{ color: 'red', fontWeight: 'bold', padding: '8px' }}>
      {statusInfo
        ? statusInfo.message
        : `Line item status: ${lineItemProblemLabel}`}
    </div>
  );

  const trackingPopoverContent = (
    <Spin spinning={isLoading || data?.loading}>
      {!!data?.tracking_number ? (
        <div>
          <Descriptions
            bordered
            style={{ maxWidth: '300px' }}
            size="small"
            column={1}
          >
            <Descriptions.Item label="Tracking Number">
              {data?.tracking_number}
            </Descriptions.Item>
            <Descriptions.Item label="Order Date">
              {data?.order_date}
            </Descriptions.Item>
            <Descriptions.Item label="Est Lead Time">
              {data?.est_lead_time} days
            </Descriptions.Item>
            <Descriptions.Item label="Est Arrival Date">
              {moment(data?.order_date)
                .add(data?.est_lead_time, 'days')
                .utcOffset('+0800')
                .format('YYYY-MM-DD')}
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <span>Tracking is not set in Supplier Payment yet</span>
      )}
    </Spin>
  );

  return (
    <Popover
      visible={visible}
      onVisibleChange={vis => setVisible(vis)}
      content={
        isSpecialStatus || hasLineItemProblem
          ? specialStatusPopoverContent
          : trackingPopoverContent
      }
      title={
        isSpecialStatus
          ? statusInfo.title
          : hasLineItemProblem
          ? 'Line Item Issue'
          : 'Tracking Number'
      }
      trigger="click"
      placement="bottom"
    >
      <li
        style={{
          cursor: 'pointer',
          alignItems: 'center',
          display: 'flex',
          gap: '4px',
          color: isSpecialStatus || hasLineItemProblem ? 'red' : undefined,
          fontWeight:
            isSpecialStatus || hasLineItemProblem ? 'bold' : undefined,
        }}
      >
        <span>
          {props.supplierPaymentId}
          {isSpecialStatus && (
            <span style={{ marginLeft: '4px' }}>{statusInfo.label}</span>
          )}
          {hasLineItemProblem && (
            <span style={{ marginLeft: '4px' }}>⚠ {lineItemProblemLabel}</span>
          )}
        </span>
        {!isSpecialStatus && !hasLineItemProblem && <DoubleRightOutlined />}
      </li>
    </Popover>
  );
};

export default SupplierPaymentPopOver;
